import type { DashboardParameter } from "@t3tools/contracts";

import type { ArtifactSpec, ArtifactWidget } from "./artifactTypes";

export type ArtifactKpiWidget = Extract<ArtifactWidget, { kind: "kpi" }>;

const GRID_COLUMNS = 12;
const HEADLINE_KPI_LIMIT = 4;

/** Static so Tailwind can see every class. */
const SPAN_CLASS: Record<number, string> = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
  7: "md:col-span-7",
  8: "md:col-span-8",
  9: "md:col-span-9",
  10: "md:col-span-10",
  11: "md:col-span-11",
  12: "md:col-span-12",
};

/**
 * Column spans on the 12-column grid. Widgets without a `span` share what the
 * explicit spans leave, with any remainder going to the first of them.
 */
export function artifactRowSpans(row: {
  readonly widgets: ReadonlyArray<{ readonly span?: number }>;
}): number[] {
  const explicit = row.widgets.reduce((sum, widget) => sum + (widget.span ?? 0), 0);
  const implicit = row.widgets.filter((widget) => widget.span === undefined).length;
  const remaining = Math.max(GRID_COLUMNS - explicit, implicit);
  const share = implicit ? Math.floor(remaining / implicit) : 0;
  let extra = implicit ? remaining % implicit : 0;
  return row.widgets.map((widget) => {
    if (widget.span !== undefined) return widget.span;
    const span = Math.max(1, Math.min(GRID_COLUMNS, share + (extra > 0 ? 1 : 0)));
    extra -= 1;
    return span;
  });
}

export function artifactSpanClassName(span: number): string {
  return SPAN_CLASS[Math.max(1, Math.min(GRID_COLUMNS, span))]!;
}

/** Ids of datasets that feed parameter options; widgets load through the resolve stream. */
export function artifactOptionDatasetIds(parameters: ReadonlyArray<DashboardParameter>): string[] {
  const ids = new Set<string>();
  for (const parameter of parameters) {
    if (parameter.kind !== "date_range" && parameter.options.from === "dataset") {
      ids.add(parameter.options.dataset);
    }
  }
  return [...ids];
}

/** The first row made only of KPIs, capped to what fits in a chat card. */
export function artifactHeadlineKpis(spec: Pick<ArtifactSpec, "rows">): ArtifactKpiWidget[] {
  const row = spec.rows.find((candidate) =>
    candidate.widgets.every((widget) => widget.kind === "kpi"),
  );
  return (row?.widgets ?? [])
    .filter((widget): widget is ArtifactKpiWidget => widget.kind === "kpi")
    .slice(0, HEADLINE_KPI_LIMIT);
}
